import { BodyText, Checkbox, Eyebrow, Stack } from '@servicetitan/design-system';
import classNames from 'classnames';
import { FC, Fragment, useMemo } from 'react';
import { isGroupedOptions, SelectOption, SelectOptionGroup } from '../../../utils/form';
import * as Styles from './select.module.less';

const DefaultOptionComponent: FC<{ opt: SelectOption<any> }> = ({ opt }) => (
    <BodyText>{opt.text}</BodyText>
);
const defaultOptionsFilter = (search: string) => (opt: SelectOption<any>) =>
    opt.text.toLowerCase().includes(search.toLowerCase());

export function SearchList<T extends number | string, O extends SelectOption<T>>({
    className,
    mobile,
    multiple,
    optionComponent,
    options,
    optionsFilter,
    opts: { allText = 'All', noneText = 'None' },
    search,
    selectAllValues,
    selectedValues,
    updateSelectedValues,
}: {
    className?: string;
    opts: {
        allText?: string;
        noneText?: string;
    };
    search: string;
    options: O[] | SelectOptionGroup<O>[];
    mobile?: boolean;
    multiple?: boolean;
    selectedValues: T[];
    optionComponent?: FC<{ opt: O }>;
    optionsFilter?(search: string): (opt: O) => boolean;
    updateSelectedValues(v: T[]): void;
    selectAllValues(): void;
}) {
    const filter = useMemo(() => {
        const searchLower = search.trim();

        return searchLower ? (optionsFilter ?? defaultOptionsFilter)(searchLower) : undefined;
    }, [optionsFilter, search]);
    const OptionComponent = optionComponent ?? DefaultOptionComponent;
    const data = useMemo(() => {
        if (!options.length) {
            return {
                groups: [],
                grouped: false,
                hasAny: false,
            };
        }

        let groupedOptions: SelectOptionGroup<O>[];
        let isGrouped = false;
        if (isGroupedOptions(options)) {
            groupedOptions = filter
                ? options
                      .map(group => ({
                          text: group.text,
                          options: group.options.filter(filter),
                      }))
                      .filter(group => !!group.options.length)
                : options;
            isGrouped = true;
        } else {
            groupedOptions = [
                {
                    text: '',
                    options: filter ? options.filter(filter) : options,
                },
            ].filter(group => !!group.options.length);
        }

        return {
            groups: groupedOptions,
            grouped: isGrouped,
            hasAny: !!options.length,
        };
    }, [options, filter]);

    const toggleValue = (val: T) => {
        if (multiple) {
            updateSelectedValues(
                selectedValues.includes(val)
                    ? selectedValues.filter(v => v !== val)
                    : [...selectedValues, val]
            );
        } else {
            updateSelectedValues([val]);
        }
    };
    const selectNoneValues = () => {
        updateSelectedValues([]);
    };
    const isSelectedAll = data.groups.every(gr =>
        gr.options.every(opt => selectedValues.includes(opt.value))
    );

    return (
        <Stack
            className={classNames(
                className,
                mobile ? Styles.searchItemContainerTakeover : Styles.searchItemContainerPopover
            )}
            direction="column"
        >
            {mobile && multiple && !!data.groups.length && (
                <Stack
                    className={Styles.searchItem}
                    onClick={isSelectedAll ? selectNoneValues : selectAllValues}
                >
                    <Stack.Item>
                        <Checkbox checked={isSelectedAll} />
                    </Stack.Item>
                    <Stack.Item fill className="m-t-half m-l-1">
                        {isSelectedAll ? noneText : allText}
                    </Stack.Item>
                </Stack>
            )}
            {data.groups.map((group, ind) => (
                <Fragment key={`${group.text}${ind.toString()}`}>
                    {data.grouped && (
                        <Eyebrow className={Styles.searchGroupTitle}>{group.text}</Eyebrow>
                    )}

                    {group.options.map(opt => (
                        <Stack
                            key={opt.value}
                            className={classNames(Styles.searchItem, {
                                [Styles.searchItemActive]:
                                    !multiple && selectedValues.includes(opt.value),
                            })}
                            onClick={() => toggleValue(opt.value)}
                        >
                            {multiple && (
                                <Checkbox
                                    className="m-r-1"
                                    checked={selectedValues.includes(opt.value)}
                                />
                            )}
                            <Stack.Item fill>
                                <OptionComponent opt={opt} />
                            </Stack.Item>
                        </Stack>
                    ))}
                </Fragment>
            ))}

            {data.groups.length === 0 && (
                <BodyText subdued className="w-100 ta-center p-y-3">
                    {data.hasAny ? 'No items found' : 'No options found'}
                </BodyText>
            )}
        </Stack>
    );
}
